import React, { useState, useEffect } from "react";
import { Box, Typography, IconButton, Tooltip, Button, CircularProgress } from "@mui/material";
import DeleteIcon from "../../assets/action-items/slideOver-delete.svg";
import { useQueryClient } from "@tanstack/react-query";
import UserInteractionsService from "../../services/interactions/Interactions";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import { TemplateItem } from "./ExistingTemplatesPopper";
import CustomConfirmation from "../Common/CustomConfirmation";
import "../../styles/templateBasedExtraction.css";
import Ask7GIconPurple from "../../assets/interaction-detail-page/selectedAsk7g_icon.svg";
import FailedIcon from "../../assets/interaction-detail-page/failed-icon.svg";
import { formatSectionTitle, isAiStatusInProgress } from "../../utils/utils";
import { DynamicSection, RunTemplateBasedExtractionProps } from "../../interfaces/templateBasedExtraction";

const getTemplateExtractionStatus = (data: any): string | undefined => {
  const payloads = [data, data?.data];
  for (const payload of payloads) {
    const status =
      payload?.aiAnalysisStatus;
    if (typeof status === "string" && status.trim()) return status.trim();
  }
  return undefined;
};

const safeRenderVal = (val: any): string => {
  if (val === null || val === undefined) return "";
  if (typeof val === "object") return JSON.stringify(val);
  return String(val);
};

export const renderSectionItem = (item: any, itemIdx: number) => {
  if (
    typeof item === "string" ||
    typeof item === "number" ||
    typeof item === "boolean"
  ) {
    return (
      <Box key={itemIdx} className="template-run-list-item">
        <span className="template-run-bullet">•</span>
        <Typography className="template-run-highlight-item">
          {String(item)}
        </Typography>
      </Box>
    );
  }

  if (item && typeof item === "object") {
    // 1. Action item pattern: { action, owner, due_date }
    const actionText = safeRenderVal(
      item.action || item.task || item.description || item.title,
    );
    const ownerText =
      item.owner !== undefined
        ? safeRenderVal(item.owner || item.assignee)
        : undefined;
    const dueDateText =
      item.due_date !== undefined
        ? safeRenderVal(item.due_date || item.dueDate || item.timing)
        : undefined;

    if (item.action !== undefined) {
      return (
        <Box key={itemIdx} className="template-run-list-item">
          <span className="template-run-bullet">•</span>
          <Typography className="template-run-highlight-item">
            <strong>{actionText}</strong>
            {ownerText && (
              <span className="template-run-item-meta">
                {" "}
                — <span className="template-run-meta-label">Owner:</span>{" "}
                {ownerText}
              </span>
            )}
            {dueDateText && dueDateText !== "Not specified" && (
              <span className="template-run-item-meta">
                {" "}
                • <span className="template-run-meta-label">Due:</span>{" "}
                {dueDateText}
              </span>
            )}
          </Typography>
        </Box>
      );
    }

    // 2. Challenge & Impact pattern: { challenge, impact }
    if (item.challenge !== undefined && item.impact !== undefined) {
      return (
        <Box key={itemIdx} className="template-run-list-item">
          <span className="template-run-bullet">•</span>
          <Typography className="template-run-highlight-item">
            <strong>{safeRenderVal(item.challenge)}:</strong>{" "}
            {safeRenderVal(item.impact)}
          </Typography>
        </Box>
      );
    }

    // 3. Label & Value pattern: { label, value } or { key, value } or { name, value }
    const labelKey = item.label || item.key || item.name;
    if (labelKey !== undefined && item.value !== undefined) {
      return (
        <Box key={itemIdx} className="template-run-list-item">
          <span className="template-run-bullet">•</span>
          <Typography className="template-run-highlight-item">
            <strong>{safeRenderVal(labelKey)}:</strong>{" "}
            {safeRenderVal(item.value)}
          </Typography>
        </Box>
      );
    }

    // 4. Other arbitrary objects: format key-value pairs nicely
    const entries = Object.entries(item).filter(
      ([_, v]) => v !== undefined && v !== null,
    );
    if (entries.length > 0) {
      return (
        <Box key={itemIdx} className="template-run-object-item">
          <span className="template-run-object-arrow">→</span>
          <Box className="template-run-object-fields">
            {entries.map(([k, v], entryIdx) => (
              <Typography
                key={`${k}-${entryIdx}`}
                className="template-run-highlight-item"
              >
                <strong>{formatSectionTitle(k)}:</strong> {safeRenderVal(v)}
              </Typography>
            ))}
          </Box>
        </Box>
      );
    }
  }

  return (
    <Box key={itemIdx} className="template-run-list-item">
      <span className="template-run-bullet">•</span>
      <Typography className="template-run-highlight-item">
        {JSON.stringify(item)}
      </Typography>
    </Box>
  );
};

export const RunTemplateBasedExtraction: React.FC<
  RunTemplateBasedExtractionProps
> = ({
  template,
  templates = [],
  interactionId,
  transcriptAssetId,
  extractionData,
  collaterals,
  isLoading = false,
  isDeleting = false,
  onEdit,
  onRegenerateEdit,
  onDelete,
  onDeleteSuccess,
  onReRun: _onReRun,
  onAssetIdResolved,
  onStatusChange,
}) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [internalAssetId, setInternalAssetId] = useState<string>(
    template.assetId || "",
  );
  const [isRetrying, setIsRetrying] = useState<boolean>(false);
  const hasStartedInitialGenerationRef = React.useRef(false);
  const componentIsMountedRef = React.useRef(true);
  const queryClient = useQueryClient();
  const { showSnackbar } = useDisplaySnackbar();
  const latestCallbacksRef = React.useRef({
    onAssetIdResolved,
    onStatusChange,
    showSnackbar,
    templateName: template.name,
  });

  React.useEffect(() => {
    componentIsMountedRef.current = true;
    return () => {
      componentIsMountedRef.current = false;
    };
  }, []);

  React.useEffect(() => {
    latestCallbacksRef.current = {
      onAssetIdResolved,
      onStatusChange,
      showSnackbar,
      templateName: template.name,
    };
  }, [onAssetIdResolved, onStatusChange, template.name]);

  const targetAssetId = template.assetId || internalAssetId || "";
  const providedExtractionAssetId =
    extractionData?.assetId || extractionData?.data?.assetId || "";

  // Check collaterals data for aiAnalysisStatus
  const matchingCollateral = React.useMemo(() => {
    if (!Array.isArray(collaterals) || collaterals.length === 0) return null;
    return (
      collaterals.find((col: any) => {
        if (col.assetType !== "template_based_extraction_v2") return false;
        if (
          template.interactionGroupId &&
          col.interactionGroupId &&
          col.interactionGroupId !== template.interactionGroupId
        ) {
          return false;
        }
        const colAssetId = col.interactionAssetId;
        const colTemplateId = col.assetMetaData?.templateId;
        if (targetAssetId || template.assetId) {
          return colAssetId === (targetAssetId || template.assetId);
        }
        if (template.isNewRun) return false;
        return Boolean(template.templateId && colTemplateId === template.templateId);
      }) || null
    );
  }, [
    collaterals,
    targetAssetId,
    template.assetId,
    template.id,
    template.templateId,
    template.interactionGroupId,
    template.isNewRun,
  ]);

  const extractionTemplateId =
    template.templateId || matchingCollateral?.assetMetaData?.templateId;
  const ownershipTemplate = templates.find(
    (item: any) => String(item.id) === String(extractionTemplateId),
  );
  const canEditTemplate = ownershipTemplate?.isOwner === true;

  const collateralAiStatus: string | undefined =
    matchingCollateral?.aiAnalysisStatus;
  const initialStatus =
    collateralAiStatus ?? (template as any)?.aiAnalysisStatus;

  const normalizedInitialStatus =
    typeof initialStatus === "string" ? initialStatus.trim().toLowerCase() : "";

  const isFailedFromCollateralOrTemplate = normalizedInitialStatus === "failed";

  const [isGenerating, setIsGenerating] = useState<boolean>(
    Boolean(template.isGenerating) && !isFailedFromCollateralOrTemplate,
  );

  React.useEffect(() => {
    if (isFailedFromCollateralOrTemplate && !isRetrying && isGenerating) {
      setIsGenerating(false);
    }
  }, [isFailedFromCollateralOrTemplate, isRetrying, isGenerating]);

  // Mutation for generating extraction V2
  const { mutateAsync: generateTemplateExtractionV2Mutation } =
    UserInteractionsService.usePostGenerateTemplateBasedExtractionV2();

  useEffect(() => {
    const effectiveAsset = template.assetId;
    if (effectiveAsset && effectiveAsset !== internalAssetId) {
      setInternalAssetId(effectiveAsset);
    }
  }, [template.assetId]);

  // Self-trigger generation when running a template without an assetId
  useEffect(() => {
    if (
      !template.shouldRegenerate &&
      !targetAssetId &&
      !hasStartedInitialGenerationRef.current &&
      !isFailedFromCollateralOrTemplate &&
      interactionId &&
      template.id &&
      template.id !== "new-template"
    ) {
      hasStartedInitialGenerationRef.current = true;
      setIsGenerating(true);
      generateTemplateExtractionV2Mutation({
        interactionId,
        transcriptAssetId: transcriptAssetId || "",
        templateId: template.templateId || template.id,
      })
        .then((res: any) => {
          if (!componentIsMountedRef.current) return;
          const resolvedAssetId =
            res?.assetId ||
            res?.data?.assetId ||
            res?.interactionAssetId ||
            res?.data?.interactionAssetId ||
            "";
          if (resolvedAssetId) {
            setInternalAssetId(resolvedAssetId);
            latestCallbacksRef.current.onAssetIdResolved?.(resolvedAssetId);
          }
          queryClient.invalidateQueries({
            queryKey: ["get_template_extraction_v2"],
          });
          queryClient.invalidateQueries({
            queryKey: ["get_interaction_collateral"],
          });
          latestCallbacksRef.current.showSnackbar(
            "success",
            `${latestCallbacksRef.current.templateName} extraction initiated`,
          );
        })
        .catch((err: any) => {
          if (!componentIsMountedRef.current) return;
          latestCallbacksRef.current.onStatusChange?.("failed");
          latestCallbacksRef.current.showSnackbar(
            "error",
            err?.response?.data?.message ||
              err?.message ||
              `Failed to run ${latestCallbacksRef.current.templateName} template extraction`,
          );
        })
        .finally(() => {
          if (componentIsMountedRef.current) setIsGenerating(false);
        });
    }
  }, [
    template.id,
    template.shouldRegenerate,
    interactionId,
    transcriptAssetId,
    targetAssetId,
    isFailedFromCollateralOrTemplate,
    generateTemplateExtractionV2Mutation,
  ]);

  // Self-trigger regeneration when running a template with shouldRegenerate
  const hasRegeneratedRef = React.useRef(false);
  useEffect(() => {
    if (
      template.shouldRegenerate &&
      !hasRegeneratedRef.current &&
      interactionId &&
      template.id &&
      template.id !== "new-template"
    ) {
      setIsGenerating(true);

      const finalAssetId =
        matchingCollateral?.interactionAssetId ||
        targetAssetId ||
        template.assetId ||
        internalAssetId ||
        providedExtractionAssetId ||
        undefined;

      // Regeneration must target the existing extraction; wait for its asset ID
      // to arrive before issuing the request.
      if (!finalAssetId) {
        setIsGenerating(false);
        return;
      }

      hasRegeneratedRef.current = true;

      generateTemplateExtractionV2Mutation({
        interactionId,
        transcriptAssetId: transcriptAssetId || "",
        templateId: template.templateId || template.id,
        assetId: finalAssetId,
        regenerate: true,
      })
        .then((res: any) => {
          if (!componentIsMountedRef.current) return;
          const resolvedAssetId =
            res?.assetId ||
            res?.data?.assetId ||
            res?.interactionAssetId ||
            res?.data?.interactionAssetId ||
            targetAssetId;
          if (resolvedAssetId && resolvedAssetId !== targetAssetId) {
            setInternalAssetId(resolvedAssetId);
            onAssetIdResolved?.(resolvedAssetId);
          }
          queryClient.invalidateQueries({
            queryKey: ["get_template_extraction_v2"],
          });
          queryClient.invalidateQueries({
            queryKey: ["get_interaction_collateral"],
          });
          showSnackbar("success", `${template.name} regeneration initiated`);
        })
        .catch((err: any) => {
          if (!componentIsMountedRef.current) return;
          latestCallbacksRef.current.onStatusChange?.("failed");
          latestCallbacksRef.current.showSnackbar(
            "error",
            err?.response?.data?.message ||
              err?.message ||
              `Failed to regenerate ${latestCallbacksRef.current.templateName} template extraction`,
          );
          setIsGenerating(false);
        });

    }
  }, [
    template.shouldRegenerate,
    template.id,
    template.templateId,
    template.name,
    interactionId,
    transcriptAssetId,
    targetAssetId,
    template.assetId,
    internalAssetId,
    matchingCollateral?.interactionAssetId,
    providedExtractionAssetId,
    onStatusChange,
    onAssetIdResolved,
    queryClient,
    generateTemplateExtractionV2Mutation,
  ]);

  // Query extraction data internally (only if not already failed, or when actively retrying)
  const shouldFetchExtraction = Boolean(targetAssetId);

  const {
    data: internalExtractionData,
    isLoading: isExtractionQueryLoading,
    isError: isExtractionQueryError,
    isSuccess: isExtractionQuerySuccess,
  } = UserInteractionsService.useGetTemplateExtractionV2(
    { assetId: targetAssetId },
    shouldFetchExtraction,
    {
      refetchInterval: (query: any) => {
        const status = getTemplateExtractionStatus(query?.state?.data);
        if (isRetrying && status?.toLowerCase() !== "completed") return 2500;
        return isAiStatusInProgress(status) ? 5000 : false;
      },
    },
  );
  const resolvedExtractionData =
    extractionData !== undefined
      ? extractionData
      : internalExtractionData;

  const extractionResponseStatus =
    getTemplateExtractionStatus(resolvedExtractionData);
  const currentAiStatus =
    extractionResponseStatus ||
    (isExtractionQuerySuccess
      ? undefined
      : collateralAiStatus ||
        (template as any)?.aiAnalysisStatus ||
        (template as any)?.status);

  const normalizedStatus =
    typeof currentAiStatus === "string"
      ? currentAiStatus.trim().toLowerCase()
      : "";

  const isCompleted = normalizedStatus === "completed";
  const isFailed =
    !isRetrying &&
    (normalizedStatus === "failed" ||
      isExtractionQueryError);

  const isExplicitInProgress = !isFailed && isAiStatusInProgress(currentAiStatus);

  const isInProgress =
    !isFailed &&
    (isRetrying ||
      isGenerating ||
      Boolean(template.isGenerating) ||
      isExplicitInProgress);

  // While in progress or loading, show the loading state ONLY (never if failed)
  const effectiveLoading = !isFailed && (isInProgress);
  useEffect(() => {
    if ((isCompleted || isFailed) && isGenerating) {
      setIsGenerating(false);
    }
  }, [isCompleted, isFailed, isGenerating]);

  useEffect(() => {
    if (isRetrying && (isCompleted || isAiStatusInProgress(currentAiStatus))) {
      setIsRetrying(false);
    }
  }, [currentAiStatus, isCompleted, isRetrying]);

  useEffect(() => {
    if (!isRetrying) return;
    const timeout = window.setTimeout(() => setIsRetrying(false), 60_000);
    return () => window.clearTimeout(timeout);
  }, [isRetrying]);

  const prevReportedStatusRef = React.useRef<string | undefined>(undefined);
  useEffect(() => {
    if (currentAiStatus && currentAiStatus !== prevReportedStatusRef.current) {
      prevReportedStatusRef.current = currentAiStatus;
      onStatusChange?.(currentAiStatus);
    }
  }, [currentAiStatus, onStatusChange]);

  // Track status transitions to show snackbars and invalidate collateral cache
  const prevAiStatusRef = React.useRef<string | undefined>(undefined);
  useEffect(() => {
    const prevStatus = prevAiStatusRef.current;
    if (
      prevStatus &&
      isAiStatusInProgress(prevStatus) &&
      isCompleted
    ) {
      queryClient.invalidateQueries({
        queryKey: ["get_interaction_collateral"],
      });
      showSnackbar("success", `${template.name} extraction completed`);
    } else if (
      prevStatus &&
      isAiStatusInProgress(prevStatus) &&
      isFailed
    ) {
      showSnackbar("error", `${template.name} extraction failed`);
    }
    prevAiStatusRef.current = currentAiStatus;
  }, [
    currentAiStatus,
    isCompleted,
    isFailed,
    template.name,
    queryClient,
  ]);

  // Mutation for deleting extraction
  const {
    mutateAsync: deleteTemplateExtractionMutation,
    isPending: isDeleteMutationPending,
  } = UserInteractionsService.useDeleteTemplateExtraction();

  const effectiveDeleting = isDeleting || isDeleteMutationPending;

  const handleInternalDelete = async () => {
    try {
      const assetIdToDelete =
        matchingCollateral?.interactionAssetId ||
        (matchingCollateral as any)?.interaction_asset_id ||
        targetAssetId ||
        template.assetId ||
        internalAssetId ||
        template.id;
      await deleteTemplateExtractionMutation({
        interactionId: interactionId || "",
        assetId: assetIdToDelete,
      });

      queryClient.invalidateQueries({
        queryKey: ["get_interaction_collateral"],
      });
      showSnackbar(
        "success",
        `${template.name} extraction deleted successfully`,
      );

      if (onDelete) {
        await onDelete(template);
      }
      onDeleteSuccess?.(template);
    } catch (err: any) {
      showSnackbar(
        "error",
        err?.response?.data?.message ||
          err?.message ||
          "Failed to delete extraction",
      );
    }
  };

  const handleRetryExtraction = async () => {
    try {
      setIsRetrying(true);
      setIsGenerating(true);
      const retryAssetId =
        matchingCollateral?.interactionAssetId ||
        (matchingCollateral as any)?.interaction_asset_id ||
        targetAssetId ||
        template.assetId ||
        internalAssetId;

      const res = await generateTemplateExtractionV2Mutation({
        interactionId: interactionId || "",
        transcriptAssetId: transcriptAssetId || "",
        templateId: template.templateId || template.id,
        assetId: retryAssetId,
        regenerate: true,
      });

      const resolvedAssetId =
        res?.assetId ||
        res?.data?.assetId ||
        res?.interactionAssetId ||
        res?.data?.interactionAssetId ||
        targetAssetId;
      if (resolvedAssetId && resolvedAssetId !== targetAssetId) {
        setInternalAssetId(resolvedAssetId);
        onAssetIdResolved?.(resolvedAssetId);
      }
      queryClient.invalidateQueries({
        queryKey: ["get_template_extraction_v2"],
      });
      queryClient.invalidateQueries({
        queryKey: ["get_interaction_collateral"],
      });
      showSnackbar("success", `${template.name} extraction retry started successfully`);
    } catch (err: any) {
      showSnackbar(
        "error",
        err?.response?.data?.message ||
          err?.message ||
          `Failed to retry ${template.name} template extraction`,
      );
      setIsGenerating(false);
      setIsRetrying(false);
    }
  };

  const handleCancelExtraction = async () => {
    const assetIdToDelete =
      matchingCollateral?.interactionAssetId ||
      targetAssetId ||
      template.assetId;
    if (!assetIdToDelete || !interactionId) {
      showSnackbar("error", "Unable to cancel extraction: missing asset details");
      return;
    }

    try {
      await deleteTemplateExtractionMutation({
        interactionId,
        assetId: assetIdToDelete,
      });

      queryClient.invalidateQueries({
        queryKey: ["get_interaction_collateral"],
      });
      showSnackbar("success", `${template.name} extraction cancelled`);
      if (onDelete) {
        await onDelete(template);
      }
      onDeleteSuccess?.(template);
    } catch (err: any) {
      showSnackbar(
        "error",
        err?.response?.data?.message ||
          err?.message ||
          "Failed to cancel extraction",
      );
    }
  };

  // Parse dynamic sections from getTemplateExtractionV2 API response (including response.value)
  const dynamicSections = React.useMemo<DynamicSection[] | null>(() => {
    if (!resolvedExtractionData) return null;

    const payload = resolvedExtractionData?.data ?? resolvedExtractionData;

    // Unpack extraction content: the response payload has "value"
    let rawContent: any =
      payload?.value !== undefined
        ? payload.value
            : payload;

    // Parse JSON string if payload or value was stringified
    if (typeof rawContent === "string") {
      const trimmed = rawContent.trim();
      if (
        (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
        (trimmed.startsWith("[") && trimmed.endsWith("]"))
      ) {
        try {
          rawContent = JSON.parse(trimmed);
        } catch {
          // keep as string
        }
      }
    }

    // If it's a plain string, show as a single summary section
    if (typeof rawContent === "string" && rawContent.trim()) {
      return [{ title: `${template.name} Summary`, text: rawContent }];
    }

    // If it has an explicit "sections" array (legacy schema)
    if (Array.isArray(rawContent?.sections) && rawContent.sections.length > 0) {
      return rawContent.sections.map((s: any) => ({
        title: s.title || s.name || s.heading || "Section",
        text: s.content || s.text || s.description || "",
        items: Array.isArray(s.items) ? s.items : undefined,
      }));
    }

    // If it's an object with key-value pairs (the current response structure)
    if (
      rawContent &&
      typeof rawContent === "object" &&
      !Array.isArray(rawContent)
    ) {
      const ignoredKeys = [
        "id",
        "assetId",
        "interactionId",
        "templateId",
        "status",
        "aiAnalysisStatus",
        "regenerate",
        "created_at",
        "createdAt",
        "updated_at",
        "updatedAt",
        "success",
      ];

      const keys = Object.keys(rawContent).filter(
        (k) => !ignoredKeys.includes(k),
      );

      if (keys.length > 0) {
        return keys.map((key) => {
          const val = rawContent[key];
          const sectionTitle = formatSectionTitle(key);

          // If the value is a plain string
          if (typeof val === "string") {
            return {
              title: sectionTitle,
              text: val,
            };
          }

          // If the value is a number or boolean
          if (typeof val === "number" || typeof val === "boolean") {
            return {
              title: sectionTitle,
              text: String(val),
            };
          }

          // If the value is an array
          if (Array.isArray(val)) {
            return {
              title: sectionTitle,
              items: val,
            };
          }

          // If the value is a nested object
          if (val && typeof val === "object") {
            const subItems = Object.entries(val).map(([subK, subV]) => {
              if (
                typeof subV === "string" ||
                typeof subV === "number" ||
                typeof subV === "boolean"
              ) {
                return {
                  label: formatSectionTitle(subK),
                  value: String(subV),
                };
              }
              return subV;
            });
            return {
              title: sectionTitle,
              items: subItems,
            };
          }

          return {
            title: sectionTitle,
            text: JSON.stringify(val),
          };
        });
      }
    }

    return null;
  }, [resolvedExtractionData, template.name]);

  const handleEditAndRegenerate = () => {
    const currentAssetId =
      matchingCollateral?.interactionAssetId ||
      (matchingCollateral as any)?.interaction_asset_id ||
      targetAssetId ||
      internalAssetId ||
      template.assetId ||
      resolvedExtractionData?.assetId ||
      resolvedExtractionData?.data?.assetId ||
      "";

    const templateToEdit: TemplateItem = {
      ...template,
      assetId: currentAssetId || template.assetId,
    };

    if (onRegenerateEdit) {
      onRegenerateEdit(templateToEdit);
    } else if (onEdit) {
      onEdit(templateToEdit);
    }
  };

  if (isExtractionQueryLoading || isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          minHeight: "calc(100vh - 280px)",
        }}
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (effectiveLoading) {
    return (
      <Box className="template-loading-container">
        <svg width="0" height="0" style={{ position: "absolute" }}>
          <defs>
            <linearGradient
              id="ask7gGradient"
              x1="2.52"
              y1="32.34"
              x2="33.6"
              y2="8.4"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0.216892" stopColor="#35013A" stopOpacity="0.06" />
              <stop offset="0.995265" stopColor="#7F018B" />
            </linearGradient>
          </defs>
        </svg>

        <Box className="prep-notes-progress-box">
          <CircularProgress
            size={70}
            thickness={3}
            sx={{
              "& .MuiCircularProgress-circle": {
                stroke: "url(#ask7gGradient)",
              },
            }}
          />
          <Box className="prep-notes-progress-icon-box">
            <img
              src={Ask7GIconPurple}
              alt="Ask7G"
              className="template-prep-notes-progress-icon"
            />
          </Box>
        </Box>
        <Typography className="template-loading-title">
          Generating template extraction{" "}
          <span className="template-loading-dots">...</span>
        </Typography>
        <Typography className="template-loading-desc">
          We're analyzing the conversation and applying the{" "}
          <strong>{template.name}</strong> structure. This may take a few
          minutes.
        </Typography>
      </Box>
    );
  }

  if (isFailed) {
    return (
      <Box className="template-failed-container">
        <Box className="template-failed-icon-box">
          <img src={FailedIcon} alt="failed" />
        </Box>
        <Typography className="template-failed-title">
          Extraction Failed
        </Typography>
        <Typography className="template-failed-subtitle">
          There was an issue. Try again
        </Typography>
        <Box className="template-failed-actions">
          <Button
            variant="contained"
            className="template-failed-cancel-btn"
            onClick={handleCancelExtraction}
            disabled={effectiveDeleting || isRetrying}
          >
            {effectiveDeleting ? "Cancelling..." : "Cancel"}
          </Button>
          <Button
            variant="outlined"
            className="template-failed-retry-btn"
            onClick={handleRetryExtraction}
            disabled={effectiveDeleting || isRetrying}
          >
            {isRetrying ? "Retrying..." : "Retry"}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="template-run-container">
      {/* Top Header */}
      <Box className="template-run-header">
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Typography className="template-run-title">
              {template.displayName || template.name} extraction
            </Typography>
          </Box>
          <Typography className="template-run-subtitle">
            Structured from the conversation using your selected template.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {canEditTemplate && (
            <Box
              className="template-edit-and-regenerate"
              onClick={handleEditAndRegenerate}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleEditAndRegenerate();
                }
              }}
            >
              <Typography className="template-edit-and-regenerate-title">
                Edit template & Regenerate
              </Typography>
            </Box>
          )}
          {/* Delete Action Button */}
          <Tooltip title="Delete extraction" arrow>
            <span>
              <IconButton
                size="small"
                onClick={() => setIsDeleteDialogOpen(true)}
                className="template-run-delete-btn"
                disabled={effectiveDeleting}
              >
                <img src={DeleteIcon} alt="Delete" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Main Extraction Results Card (TemplateWorkspace) */}
      <Box className="template-run-card">
        {dynamicSections &&
          dynamicSections.length > 0 &&
          dynamicSections.map((sec: DynamicSection, idx: number) => (
            <Box
              key={sec.title + idx}
              className={`template-run-section ${
                idx === dynamicSections.length - 1
                  ? "template-run-section-last"
                  : ""
              }`}
            >
              <Typography className="template-run-section-title">
                {sec.title}
              </Typography>
              {sec.text && (
                <Typography className="template-run-section-text">
                  {sec.text}
                </Typography>
              )}
              {sec.items &&
                Array.isArray(sec.items) &&
                sec.items.length > 0 && (
                  <Box className="template-run-key-highlights">
                    {sec.items.map((item: any, itemIdx: number) =>
                      renderSectionItem(item, itemIdx),
                    )}
                  </Box>
                )}
            </Box>
          ))}
      </Box>

      {/* Delete Confirmation Dialog */}
      <CustomConfirmation
        isOpen={isDeleteDialogOpen}
        title="Delete template extraction?"
        message={`Are you sure you want to delete the "${template.name}" extraction? This action cannot be undone.`}
        actionType="delete"
        actionBtnLabel="Delete"
        cancelBtnLabel="Cancel"
        isLoading={effectiveDeleting}
        width="350px"
        handleClose={() => {
          if (!effectiveDeleting) {
            setIsDeleteDialogOpen(false);
          }
        }}
        handleSuccess={async () => {
          try {
            await handleInternalDelete();
          } finally {
            setIsDeleteDialogOpen(false);
          }
        }}
      />
    </Box>
  );
};

export const runTemplateBasedExtraction = RunTemplateBasedExtraction;
export default RunTemplateBasedExtraction;
